import { Node, mergeAttributes } from "@tiptap/core";
import type { Selection } from "@tiptap/pm/state";
import { NodeSelection, TextSelection } from "@tiptap/pm/state";

export type AlignmentDirection = "left" | "center" | "right";

export type AlignmentOptions = { directions: AlignmentDirection[] };

declare module "@tiptap/core" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Commands<ReturnType> {
    alignment: {
      setAlignment: (attrs: { direction: AlignmentDirection }) => ReturnType;
      removeAlignment: () => ReturnType;
      toggleAlignment: (attrs: { direction: AlignmentDirection }) => ReturnType;
    };
  }
}

const directionFromElement = (
  element: HTMLElement,
  directions: AlignmentDirection[],
) => {
  const textAlign = element.style?.textAlign ?? "";
  if (!directions.includes(textAlign as AlignmentDirection)) {
    return null;
  }
  return textAlign as AlignmentDirection;
};

const MEDIA_NODE_NAMES = new Set(["image", "video", "iframe"]);

/**
 * Alignment wraps block content. Inline media (video) can't be wrapped directly —
 * expand the selection to the enclosing block first. Block media (image/iframe)
 * can be wrapped as-is via NodeSelection.
 */
const selectionForAlignmentWrap = (selection: Selection) => {
  if (!(selection instanceof NodeSelection)) {
    return null;
  }

  const node = selection.node;
  if (!MEDIA_NODE_NAMES.has(node.type.name)) {
    return null;
  }

  // Block media: keep the NodeSelection — wrapIn will wrap that atom.
  if (!node.type.isInline) {
    return null;
  }

  // Inline media: wrap the parent block (usually a paragraph) so text-align applies.
  const $from = selection.$from;
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    const parent = $from.node(depth);
    if (parent.type.isBlock && parent.type.name !== "alignment") {
      return TextSelection.create(
        selection.$from.doc,
        $from.before(depth),
        $from.after(depth),
      );
    }
  }

  return null;
};

/*
Alignment is stored as a `div` wrapper carrying a `text-align` style, matching the HTML that the
previous editor produced (and what the sanitiser allows on `div`). A wrapper node is used instead of
TipTap's TextAlign extension because that writes the style onto the paragraph/heading itself, which
the sanitiser would strip.

Images/videos/iframes participate via CSS (`inline-flex` / `inline-block` on media + resize
containers) so the wrapper's text-align actually shifts them.
*/
export const Alignment = Node.create<AlignmentOptions>({
  name: "alignment",

  group: "block",

  // Allow a single media atom or mixed block content inside the wrapper.
  content: "block+",

  defining: true,

  addOptions() {
    return { directions: ["left", "center", "right"] };
  },

  addAttributes() {
    return {
      direction: {
        default: "left",
        parseHTML: (element) =>
          directionFromElement(element, this.options.directions) ?? "left",
        renderHTML: (attributes) => ({
          style: `text-align: ${attributes.direction as string};`,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div",
        priority: 45,
        getAttrs: (element) => {
          const direction = directionFromElement(
            element as HTMLElement,
            this.options.directions,
          );
          return direction ? { direction } : false;
        },
      },
      {
        tag: "p",
        priority: 105,
        getAttrs: (element) => {
          const direction = directionFromElement(
            element as HTMLElement,
            this.options.directions,
          );
          return direction ? { direction } : false;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setAlignment:
        ({ direction }) =>
        ({ editor, chain, state }) => {
          if (editor.isActive(this.name)) {
            if (editor.isActive(this.name, { direction })) {
              return true;
            }
            return chain().updateAttributes(this.name, { direction }).run();
          }

          const expanded = selectionForAlignmentWrap(state.selection);
          let next = chain();
          if (expanded) {
            next = next.setTextSelection({
              from: expanded.from,
              to: expanded.to,
            });
          }

          return next.wrapIn(this.name, { direction }).run();
        },
      removeAlignment:
        () =>
        ({ editor, chain }) => {
          if (!editor.isActive(this.name)) {
            return false;
          }
          return chain().lift(this.name).run();
        },
      toggleAlignment:
        (attrs) =>
        ({ commands }) =>
          commands.toggleWrap(this.name, attrs),
    };
  },
});
